import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import axios from "axios";

const API_URL =
  "https://ritehomepros.com/marketing_leads_backend/enquiry/create/";

const BUSINESS_ID = "e3896a07-6db2-4e91-a48f-9a25a9d07e9d";

const EstimateSection = () => {
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    fname: "",
    lname: "Customer",
    email: "",
    mobile: "",
    zip: "",
    service: "",
  });

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ================= VALIDATION ================= */
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateForm = () => {
    if (!form.service) return "Please select a service";
    if (!form.fname.trim()) return "Name is required";
    if (!emailRegex.test(form.email)) return "Enter a valid email";
    if (form.mobile.length !== 10)
      return "Enter a valid 10-digit phone number";
    if (form.zip.length !== 5)
      return "Enter a valid 5-digit ZIP code";
    return null;
  };

  /* ================= HANDLERS ================= */
  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    handleChange("mobile", value);
  };

  const handleZipChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 5);
    handleChange("zip", value);
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      setSnack({ open: true, message: error, severity: "error" });
      return;
    }

    try {
      setLoading(true);
      await axios.post(API_URL, {
        business: BUSINESS_ID,
        fname: form.fname,
        lname: form.lname,
        mobile: form.mobile,
        zip: form.zip,
        source: "business_website",
        email: form.email,
        service: form.service,
      });

      setSnack({
        open: true,
        message: "Estimate request submitted successfully!",
        severity: "success",
      });

      setForm({
        fname: "",
        lname: "Customer",
        email: "",
        mobile: "",
        zip: "",
        service: "",
      });
    } catch (err) {
      setSnack({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: -8, mb: 10, position: "relative" }}>
        <Box
          sx={{
            bgcolor: "#ffffff",
            borderRadius: 4,
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 5 },
            boxShadow: "0 26px 80px rgba(15,23,42,0.08)",
          }}
        >
          <Typography align="center" fontSize={24} fontWeight={800} mb={3}>
            Request an Estimate
          </Typography>

          {/* SERVICE SELECTION */}
          <Typography align="center" fontWeight={700} mb={2}>
            What service are you looking for?
          </Typography>

          <RadioGroup
            row
            value={form.service}
            onChange={(e) => handleChange("service", e.target.value)}
            sx={{ justifyContent: "center", mb: 4, gap: 2 }}
          >
            {[
              "Water Mitigation",
              "HVAC Services",
              "Cleaning Services",
              "Roofing Services",
            ].map((service) => (
              <FormControlLabel
                key={service}
                value={service}
                control={<Radio />}
                label={service}
              />
            ))}
          </RadioGroup>

          {/* USER FORM */}
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Your Name"
                value={form.fname}
                onChange={(e) => handleChange("fname", e.target.value)}
                InputProps={{
                  endAdornment: <PersonOutlineIcon />,
                  sx: inputPillStyles,
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Phone Number"
                value={form.mobile}
                onChange={handlePhoneChange}
                InputProps={{
                  endAdornment: <PhoneInTalkOutlinedIcon />,
                  sx: inputPillStyles,
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Email Address"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                InputProps={{
                  endAdornment: <EmailOutlinedIcon />,
                  sx: inputPillStyles,
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Zip Code"
                value={form.zip}
                onChange={handleZipChange}
                InputProps={{
                  endAdornment: <HomeOutlinedIcon />,
                  sx: inputPillStyles,
                }}
              />
            </Grid>

            <Grid item xs={12} textAlign="center" mt={3}>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  borderRadius: 999,
                  px: 6,
                  py: 1.6,
                  bgcolor: "#0d78ff",
                  color: "#fff",
                  fontWeight: 800,
                  "&:hover": { bgcolor: "#0561e8" },
                }}
              >
                {loading ? "Submitting..." : "Submit Request"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const inputPillStyles = {
  borderRadius: "999px",
  height: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e3e5ec",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#0d78ff",
  },
  px: 2.2,
  fontSize: 14,
};

export default EstimateSection;
